import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { PetitionCTA } from "@/components/PetitionCTA";

const NAV = [
  { label: "Waarom 80?", href: "/#waarom-80", id: "nav-why" },
  { label: "De feiten", href: "/#feiten", id: "nav-facts" },
  { label: "FAQ", href: "/#faq", id: "nav-faq" },
];

export const Wordmark = ({ dark = false }) => (
  <Link
    to="/"
    data-testid="wordmark-link"
    className={`flex items-center gap-2.5 whitespace-nowrap font-display text-base font-semibold tracking-tight md:text-lg ${
      dark ? "text-gooi-offwhite" : "text-gooi-green"
    }`}
    aria-label="Rust langs de A1 — naar boven"
  >
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gooi-green font-display text-sm font-bold text-gooi-offwhite">
      80
    </span>
    Rust langs de A1
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-gooi-offwhite/85 shadow-[0_1px_0_rgba(28,59,43,0.1)] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[90rem] items-center justify-between px-5 py-4 md:px-12">
        <Wordmark />
        <nav className="hidden items-center gap-8 md:flex" aria-label="Hoofdnavigatie">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={item.id}
              className="text-sm font-semibold text-gooi-green/80 transition-colors duration-300 hover:text-gooi-green"
            >
              {item.label}
            </a>
          ))}
          <PetitionCTA location="header" size="sm" />
        </nav>
        <div className="flex items-center gap-3 md:hidden">
          <PetitionCTA location="header_mobile" size="sm" />
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Menu sluiten" : "Menu openen"}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-gooi-green/20 text-gooi-green"
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
            className="overflow-hidden border-t border-gooi-green/10 bg-gooi-offwhite/95 backdrop-blur-xl md:hidden"
            aria-label="Mobiele navigatie"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  data-testid={`${item.id}-mobile`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-semibold text-gooi-green transition-colors duration-300 hover:bg-gooi-sand"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
